import type { CVData } from '~/types'

/**
 * The CVs a visitor already has, for step 1 of the tailoring tool on
 * /tailor-resume-to-job (MarketingTailorTool) and for the ATS check
 * (AtsCvChecker), so they are not asked to paste a CV this site made for them:
 *
 * - Signed in: their CVs in the account, newest first.
 * - A CV made here without an account, in this browser: the one the e-mailed
 *   link opens (/free-cv-builder?draft=…&sig=…), read with that link's
 *   signature.
 *
 * The account-less CV is remembered when it is built (useCvChat,
 * LandingFreeCvBuilder, the tool itself): its id and the link's signature,
 * which the build returns. The signature only opens the CV this browser
 * already had.
 */
const GUEST_CV_KEY = 'lastGuestCv'

/** Same as GUEST_CV_DAYS in server/utils/guestCvLink.ts: the CV is deleted after that. */
const GUEST_CV_MS = 30 * 24 * 60 * 60 * 1000

export interface RememberedGuestCv {
  id: string
  sig: string
  /** The layout last picked on the result, so the CV reopens in it. */
  template?: string
  expiresAt: string
}

function read(): RememberedGuestCv | null {
  if (!import.meta.client) return null
  try {
    const saved = JSON.parse(localStorage.getItem(GUEST_CV_KEY) ?? 'null') as Partial<RememberedGuestCv> | null
    if (!saved || typeof saved.id !== 'string' || typeof saved.sig !== 'string' || typeof saved.expiresAt !== 'string') return null
    if (new Date(saved.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(GUEST_CV_KEY)
      return null
    }
    return saved as RememberedGuestCv
  } catch {
    return null
  }
}

function write(cv: RememberedGuestCv) {
  try {
    localStorage.setItem(GUEST_CV_KEY, JSON.stringify(cv))
  } catch {
    // Private mode: the feature page simply leads to the sign-up.
  }
}

/** A CV just made (or reopened) without an account; `expiresAt` defaults to a fresh 30 days. */
export function rememberGuestCv(id: string | null | undefined, sig: string | null | undefined, expiresAt?: string) {
  if (!import.meta.client || !id || !sig) return
  const template = read()?.id === id ? read()?.template : undefined
  write({ id, sig, template, expiresAt: expiresAt ?? new Date(Date.now() + GUEST_CV_MS).toISOString() })
}

/** The layout picked on the result of the remembered CV. */
export function rememberGuestCvTemplate(id: string | null | undefined, template: string) {
  const saved = read()
  if (!saved || saved.id !== id) return
  write({ ...saved, template })
}

/** The link's signature for this CV, when it is the one this browser remembers. */
export function guestCvSig(id: string | null | undefined): string | null {
  const saved = read()
  return saved && saved.id === id ? saved.sig : null
}

/** The remembered CV is gone from the server (expired or deleted). */
export function forgetGuestCv(id: string) {
  if (read()?.id !== id) return
  try {
    localStorage.removeItem(GUEST_CV_KEY)
  } catch {
    /* ignore */
  }
}

/** An account CV to pick in the tool. */
export interface AccountCv {
  id: string
  name: string
  title: string
  createdAt: string
}

/** How many of the account's CVs the tool offers; older ones are opened in the editor. */
const ACCOUNT_CVS = 6

export function useTailorEntry() {
  const supabase = useSupabaseClient()
  const user = useSupabaseUser()

  /** Read after mount: the server renders the tool without them, so hydration matches. */
  const accountCvs = ref<AccountCv[]>([])
  const guestCv = ref<RememberedGuestCv | null>(null)

  async function loadAccountCvs(userId: string | undefined) {
    accountCvs.value = []
    if (!userId) return
    const { data } = await supabase
      .from('cv_requests')
      .select('id, parsed_data, created_at')
      .eq('user_id', userId)
      .is('deleted_at', null)
      // A chat interview still in progress (or a failed import) has nothing to tailor yet.
      .or('status.is.null,status.not.in.(draft,failed)')
      .order('created_at', { ascending: false })
      .limit(ACCOUNT_CVS)
      .returns<{ id: string, parsed_data: CVData | null, created_at: string }[]>()
    accountCvs.value = (data ?? [])
      .filter(row => row.parsed_data)
      .map(row => ({ id: row.id, name: row.parsed_data?.name?.trim() ?? '', title: row.parsed_data?.title?.trim() ?? '', createdAt: row.created_at }))
  }

  /** One account CV in full, or null when it cannot be read. */
  async function loadAccountCv(id: string): Promise<CVData | null> {
    const { data } = await supabase
      .from('cv_requests')
      .select('parsed_data')
      .eq('id', id)
      .maybeSingle<{ parsed_data: CVData | null }>()
    return data?.parsed_data ?? null
  }

  onMounted(() => {
    guestCv.value = read()
    watch(() => user.value?.id, loadAccountCvs, { immediate: true })
  })

  return { accountCvs, guestCv, loadAccountCv }
}
